import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { HeartHandshake, LogOut, ShieldUser } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/utils';

import * as systemApi from '@/api/system';
import { useAuth } from '@/context/AuthContext';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { activeNavItem, onSurface, useNavItems } from '@/shared/layout/navItems';
import { useTravellingUnderline } from '@/shared/hooks/useTravellingUnderline';
import NavUnderline from '@/shared/layout/NavUnderline';
/*
 * Loaded on demand. The dialog pulls in a QR code generator worth about twenty
 * kilobytes, and most readers never open it — there is no reason for everyone
 * to download it on first paint.
 */
const DonateModal = React.lazy(() => import('@/shared/layout/DonateModal'));
import ThemeToggle from '@/shared/layout/ThemeToggle';

type HeaderProps = {
    onOpenProfile?: () => void;
};

/**
 * Header is the application's top bar.
 *
 * It carries the section navigation on a desktop; on a phone that job belongs to
 * the bottom bar, so all that stays up here is the logo, the donate link, the
 * language and the theme — and the bar itself gets out of the way as the reader
 * scrolls down a long list.
 */
const Header: React.FC<HeaderProps> = ({ onOpenProfile }) => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
    const [donateMethods, setDonateMethods] = useState<systemApi.DonateMethod[]>([]);
    const [hiddenByScroll, setHiddenByScroll] = useState(false);
    const lastScrollYRef = useRef(0);

    const isMobile = useMediaQuery('(max-width: 600px)');

    // Coming back to the narrow layout brings the bar back. Only the scroll
    // handler hides it, and it only runs while narrow, so a value left over from
    // an earlier narrow session would otherwise keep the bar hidden until the
    // reader happened to scroll.
    const [wasMobile, setWasMobile] = useState(isMobile);
    if (wasMobile !== isMobile) {
        setWasMobile(isMobile);
        setHiddenByScroll(false);
    }

    const allItems = useNavItems(Boolean(user?.is_superuser));
    const navItems = onSurface(allItems, 'header');
    const current = activeNavItem(allItems, location.pathname);
    /** Shown as an icon on a phone, where the header has no room for the row. */
    const adminItem = navItems.find((item) => item.id === 'admin');

    // One bar that travels between the links; see the hook for why the row has
    // to be measured rather than styled. The shape covers a language change and
    // the admin link appearing for a superuser.
    const {
        containerRef: navRef,
        setItemRef,
        box: underline,
        placed,
    } = useTravellingUnderline<HTMLAnchorElement>(
        current?.id,
        navItems.map((item) => item.id + item.label).join('|') + String(isMobile),
    );

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Whether there is anything to offer at all — and, since the answer is the
    // list itself, the list. It used to keep only whether the list was empty
    // and let the dialog fetch it again when opened, which meant the dialog
    // opened as a title with nothing under it and filled in when the second
    // request landed.
    useEffect(() => {
        let cancelled = false;
        systemApi
            .getDonateMethods()
            .then((methods) => {
                if (!cancelled) setDonateMethods(methods ?? []);
            })
            .catch(() => {
                // Nothing to offer if we cannot find out.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isMobile) {
            return;
        }

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const previous = lastScrollYRef.current;

            // Near the top the bar always shows; below that it follows the
            // direction of travel, so reading down a list gives back the space.
            setHiddenByScroll(currentScrollY >= 50 && currentScrollY > previous);

            lastScrollYRef.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobile]);

    const donateButton =
        donateMethods.length === 0 ? null : (
            <button
                type="button"
                onClick={() => setIsDonateModalOpen(true)}
                className={cn(
                    /*
                      Filled rather than lettered, and a pill rather than a bar.
                      It sat in the same grey as the section links, at the same
                      height, immediately after them — so it read as a fourth
                      section while behaving like none of them: it opens a dialog
                      rather than going anywhere. The shape says which of the two
                      it is before the colour does.

                      No dark: variant, because the header has no light one. It
                      is the chrome colour under both themes — a dark band across a
                      light page or a dark one — so this is the same button
                      either way.
                    */
                    'flex items-center gap-1.5 rounded-full font-medium uppercase',
                    'bg-green-400 text-green-950 hover:bg-green-300',
                    'focus-visible:ring-2 focus-visible:ring-green-300 focus-visible:ring-offset-2',
                    'focus-visible:ring-offset-chrome focus-visible:outline-none',
                    isMobile ? 'h-8 px-2.5 text-[0.7rem]' : 'h-9 px-3 text-sm',
                )}
            >
                <HeartHandshake className={isMobile ? 'size-4' : 'size-5'} />
                {t('donate', 'Донат')}
            </button>
        );

    return (
        <header
            className={cn(
                'w-full bg-chrome text-chrome-foreground',
                isMobile
                    ? 'fixed inset-x-0 top-0 z-chrome-top transition-transform duration-300'
                    : 'static',
                isMobile && hiddenByScroll && '-translate-y-full',
            )}
        >
            <div className={cn('flex items-center px-4', isMobile ? 'h-12' : 'h-16')}>
                {isMobile ? (
                    <div className="flex w-full items-center justify-between">
                        <Link
                            to="/books/page/1"
                            aria-label={t('booksTab')}
                            className="flex items-center"
                        >
                            <img src="/logo.png" alt="" className="size-6" />
                        </Link>
                        <div className="flex items-center gap-0.5">
                            {donateButton}
                            {/*
                              The narrow header carries no section links, so the
                              one section that belongs to this surface and not to
                              the bar at the foot needs saying here or it cannot
                              be reached from a phone at all — which is how it
                              stood between taking it out of the bar and this.

                              Taken from the same list rather than gated on
                              is_superuser again: it is in that list only for a
                              superuser, so who sees it stays decided in one
                              place.
                            */}
                            {adminItem && (
                                <Link
                                    to={adminItem.path}
                                    aria-label={adminItem.label}
                                    title={adminItem.label}
                                    aria-current={current?.id === 'admin' ? 'page' : undefined}
                                    className={cn(
                                        'flex size-8 items-center justify-center rounded',
                                        current?.id === 'admin'
                                            ? 'text-chrome-foreground'
                                            : 'text-chrome-muted hover:text-chrome-foreground',
                                    )}
                                >
                                    <ShieldUser className="size-5" />
                                </Link>
                            )}
                            <ThemeToggle />
                        </div>
                    </div>
                ) : (
                    <>
                        {/*
                          These navigate rather than switch panels, so they are
                          links marked with aria-current — not tabs, which would
                          promise tabpanels that do not exist.
                        */}
                        <nav
                            ref={navRef}
                            aria-label={t('booksTab')}
                            className="relative flex items-center self-stretch"
                        >
                            {navItems.map((item) => (
                                <Link
                                    key={item.id}
                                    to={item.path}
                                    ref={setItemRef(item.id)}
                                    aria-current={current?.id === item.id ? 'page' : undefined}
                                    className={cn(
                                        // The transparent border stays on every
                                        // link so the row keeps the height it
                                        // had when the border was the mark.
                                        'flex items-center gap-2 self-stretch border-b-2 border-transparent px-4',
                                        'text-sm font-medium uppercase tracking-wide',
                                        'transition-colors duration-200 motion-reduce:transition-none',
                                        current?.id === item.id
                                            ? 'text-chrome-foreground'
                                            : 'text-chrome-muted hover:text-chrome-foreground',
                                    )}
                                >
                                    {item.id === 'books' && (
                                        <img src="/logo.png" alt="" className="size-6" />
                                    )}
                                    {item.label}
                                </Link>
                            ))}
                            <NavUnderline
                                box={underline}
                                placed={placed}
                                className="bg-chrome-foreground"
                            />
                            <div className="ml-4 flex items-center">{donateButton}</div>
                        </nav>

                        <div className="flex-1" />

                        <div className="flex items-center gap-1">
                            <ThemeToggle />
                            <Button
                                variant="ghost"
                                onClick={() => onOpenProfile?.()}
                                className="text-chrome-foreground hover:bg-chrome-foreground/10 hover:text-chrome-foreground focus-visible:ring-chrome-muted"
                            >
                                {user?.username}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleLogout}
                                title={t('logout', 'Выход')}
                                aria-label={t('logout', 'Выход')}
                                className="text-chrome-foreground hover:bg-chrome-foreground/10 hover:text-chrome-foreground focus-visible:ring-chrome-muted"
                            >
                                <LogOut className="size-5" />
                            </Button>
                        </div>
                    </>
                )}
            </div>

            {isDonateModalOpen && (
                <Suspense fallback={null}>
                    <DonateModal
                        open
                        methods={donateMethods}
                        onClose={() => setIsDonateModalOpen(false)}
                    />
                </Suspense>
            )}
        </header>
    );
};

export default Header;
